import { MembersEmptyPage } from "@components/common/empty-page/members-empty-page";
import { WORKSPACE_MEMBERS_TABLE_COLUMNS } from "@components/table/members/members-table-constants";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { MembersTable } from "@components/table/members/members-table";
import { MembersSort } from "@components/table/members/members-sort";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { InviteMemberToCompany } from "@pages/members/invite-member-to-company";
import { MainMemberDetails } from "@pages/members/main-member-details";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useCompanyMemberBulkActions } from "@src/pages/members/use-company-member-bulk-actions";
import { MembersSortBy } from "@custom-types/member-types";
import { isMembersSortBy } from "@custom-types/type-guards";
import {
  fetchedMembersFlagsSelector,
  fetchingMembersFlagsSelector,
  filteredCompanyMembersSelector,
} from "@store/members/members-selector";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { searchSelector } from "@store/ui/ui-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { getExportComponent, sortMembers } from "@utils/member-utils";
import { useEffect, useMemo } from "react";
import { MembersMobileView } from "@pages/members/members-mobile-view";
import { resetTableState } from "@store/table/table-slice";
import { DEFAULT_WORKSPACE_MEMBERS_SORT_BY } from "@components/table/members/members-sort-constants";

/** Listing all the members of a company in a table */
export function MembersPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const { isScreenSmOrSmaller } = useMediaQueryList();

  useCompanyMembers();

  const currentUser = useAppSelector(currentUserSelector);
  const filteredCompanyMembers = useAppSelector(filteredCompanyMembersSelector);
  const { isFetchingCompanyMembers } = useAppSelector(
    fetchingMembersFlagsSelector
  );
  const { hasFetchedWorkspaceMembers } = useAppSelector(
    fetchedMembersFlagsSelector
  );
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);

  const { debouncedSearchText } = useAppSelector(searchSelector);
  const { sortBy, setSortBy } = useSubjectsSortByQuery<MembersSortBy>({
    defaultSort: DEFAULT_WORKSPACE_MEMBERS_SORT_BY,
    isValidSort: isMembersSortBy,
  });
  const sortedMembers = sortMembers(filteredCompanyMembers, sortBy);

  /**
   * Decides whether to show the empty page or not.
   * The empty page component handles the two different cases:
   * - No members at all (only the current user is in the workspace)
   * - No members that match the search
   */
  const shouldShowEmptyPage = useMemo(() => {
    if (isFetchingCompanyMembers || !hasFetchedWorkspaceMembers) {
      return false;
    }

    // If there are no members, show the empty page
    if (sortedMembers.length === 0) {
      return true;
    }

    if (debouncedSearchText.length) {
      // If there are no members that match the search, show the empty page
      // Here if the only member is the current user we don't show the empty page,
      // only if there are no members at all
      return !sortedMembers.length;
    }

    if (sortedMembers.length === 1) {
      // If the only member is the current user, show the empty page
      return sortedMembers[0].identity === currentUser?.identity;
    }

    return false;
  }, [
    sortedMembers,
    isFetchingCompanyMembers,
    hasFetchedWorkspaceMembers,
    currentUser,
    debouncedSearchText,
  ]);

  const buttonComponents = useCompanyMemberBulkActions({
    members: sortedMembers,
    company: selectedSdbCompany,
  });

  useEffect(() => {
    return () => {
      dispatch(resetTableState());
    };
  }, [dispatch]);

  return (
    <>
      {!isScreenSmOrSmaller && <MainMemberDetails />}

      <EntitiesToolbar
        toolbarEntityName="Member"
        numberOfRows={sortedMembers.length}
        isLoading={isFetchingCompanyMembers}
        sortActions={
          sortedMembers.length > 0 && (
            <MembersSort
              sortBy={sortBy}
              setSortBy={setSortBy}
              tableSubject="workspace"
            />
          )
        }
        exportComponent={getExportComponent(
          sortedMembers.length,

          buttonComponents.export
        )}
      />
      {shouldShowEmptyPage && (
        <MembersEmptyPage
          subtitle="All of your workspace members will be found here. You can add members to your workspace by inviting them."
          button={<InviteMemberToCompany buttonVariant="filled" />}
        />
      )}

      {isScreenSmOrSmaller && !shouldShowEmptyPage && (
        <MembersMobileView
          bulkActionButtons={buttonComponents}
          members={sortedMembers}
          isLoading={isFetchingCompanyMembers}
          tableSubject="workspace"
          sortBy={sortBy}
        />
      )}

      {!isScreenSmOrSmaller && !shouldShowEmptyPage && (
        <ColumnPreferencesProvider
          tableKey="workspace-members-table"
          columns={WORKSPACE_MEMBERS_TABLE_COLUMNS}
          options={{
            nonConfigurableColumns: ["options"],
            defaultVisibleColumns: ["user", "email", "status", "role"],
          }}
        >
          <MembersTable
            companyId={companyId}
            members={sortedMembers}
            tableSubject="workspace"
            isLoading={isFetchingCompanyMembers}
            bulkActionButtons={buttonComponents}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        </ColumnPreferencesProvider>
      )}
    </>
  );
}
